import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Filter, ArrowRight, Globe, Coins, Palette } from 'lucide-react';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const PortfolioPage = () => {
  const { portfolio } = mockData;
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = [
    { name: 'All', icon: Filter, count: portfolio.length },
    { name: 'Website', icon: Globe, count: portfolio.filter(item => item.category === 'Website').length },
    { name: 'Blockchain', icon: Coins, count: portfolio.filter(item => item.category === 'Blockchain').length },
    { name: 'Tasarım', icon: Palette, count: portfolio.filter(item => item.category === 'Tasarım').length }
  ];

  const filteredPortfolio = activeFilter === 'All'
    ? portfolio
    : portfolio.filter(item => item.category === activeFilter);

  const stats = [
    { number: '50+', label: 'Tamamlanan Proje' },
    { number: '30+', label: 'Mutlu Müşteri' },
    { number: '15+', label: 'Token Projesi' },
    { number: '100%', label: 'Müşteri Memnuniyeti' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO 
        title="Portfolyo | Başarı Hikayelerimiz"
        description="Viral Strateji tarafından hayata geçirilen web tasarım, blockchain ve dijital pazarlama projelerini inceleyin."
        keywords="portfolyo, tasarım örnekleri, blockchain projeleri, web sitesi referansları, dijital işler"
      />
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Çalışma Portfolyomuz
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Farklı sektörlerde gerçekleştirdiğimiz başarılı projelerimizi keşfedin.
              Her proje kendine özgü yaratıcı çözümler ve teknik mükemmellik içerir.
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-full font-semibold text-base hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>Proje Başlat</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter, index) => {
              const Icon = filter.icon;
              return (
                <button
                  key={index}
                  onClick={() => setActiveFilter(filter.name)}
                  className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold transition-all duration-300 ${activeFilter === filter.name
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{filter.name}</span>
                  <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
                    {filter.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPortfolio.map((project, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100">
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <p className="text-white text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${project.category === 'Website' ? 'bg-blue-100 text-blue-800' :
                      project.category === 'Blockchain' ? 'bg-purple-100 text-purple-800' :
                        'bg-pink-100 text-pink-800'
                      }`}>
                      {project.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {project.description}
                  </p>
                  <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-full font-medium opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:from-blue-700 hover:to-purple-700">
                    Detayları Görüntüle
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* No results message */}
          {filteredPortfolio.length === 0 && (
            <div className="text-center py-20">
              <div className="text-gray-400 text-6xl mb-4">📁</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Bu kategoride henüz proje yok
              </h3>
              <p className="text-gray-600">
                Başka kategorileri kontrol edebilir veya yakında eklenecek projeler için bekleyebilirsiniz.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Kullandığımız Teknolojiler
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern ve güncel teknolojilerle güçlü çözümler geliştiriyoruz.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { name: 'React', icon: '⚛️' },
              { name: 'Node.js', icon: '🟢' },
              { name: 'Solidity', icon: '🔷' },
              { name: 'WordPress', icon: '🅦' },
              { name: 'Figma', icon: '🎨' },
              { name: 'MongoDB', icon: '🍃' }
            ].map((tech, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="text-4xl mb-3">{tech.icon}</div>
                <h3 className="font-semibold text-gray-900">{tech.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Siz de Portfolyomuzda Yer Almak İster misiniz?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Uzman ekibimizle çalışarak başarılı projelerimize katılın. Her proje özenle tasarlanır ve mükemmel sonuçlar elde edilir.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-6 py-2.5 rounded-full font-semibold text-base hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              <span>Proje Başlat</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-6 py-2.5 rounded-full font-semibold text-base hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105"
            >
              <span>Hizmetleri Keşfet</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;